import { ref, onMounted, onUnmounted } from 'vue';

export function useScannerDetection(options) {
    const defaults = {
        onComplete                  : false,
        onError                     : false,
        onReceive                   : false,
        onKeyDetect                 : false,
        timeBeforeScanTest          : 100,
        avgTimeByChar               : 30,
        minLength                   : 6,
        endChar                     : [9, 13],
        startChar                   : [],
        ignoreIfFocusOn             : false,
        scanButtonKeyCode           : false,
        scanButtonLongPressThreshold: 3,
        onScanButtonLongPressed     : false,
        stopPropagation             : false,
        preventDefault              : false,
    };

    const mergedOptions = { ...defaults, ...options };
    const firstCharTime = ref(0);
    const lastCharTime = ref(0);
    const stringWriting = ref('');
    const callIsScanner = ref(false);
    const testTimer = ref(null);
    const scanButtonCounter = ref(0);
    const initScannerDetection = () => {
        firstCharTime.value = 0;
        stringWriting.value = '';
        scanButtonCounter.value = 0;
    };
    const isFocusOnIgnoredElement = () => {
        if (!mergedOptions.ignoreIfFocusOn) return false;
        const focusedElement = document.activeElement;
        if (typeof mergedOptions.ignoreIfFocusOn === 'string') {
            return focusedElement.matches(mergedOptions.ignoreIfFocusOn);
        }
        if (Array.isArray(mergedOptions.ignoreIfFocusOn)) {
            return mergedOptions.ignoreIfFocusOn.some(selector => focusedElement.matches(selector));
        }
        return false;
    };
    const scannerDetectionTest = (s) => {
        if (s) {
            firstCharTime.value = lastCharTime.value = 0;
            stringWriting.value = s;
        }
        if (!scanButtonCounter.value) {
            scanButtonCounter.value = 1;
        }
        if (stringWriting.value.length >= mergedOptions.minLength && lastCharTime.value - firstCharTime.value < stringWriting.value.length * mergedOptions.avgTimeByChar) {
            if (mergedOptions.onScanButtonLongPressed && scanButtonCounter.value > mergedOptions.scanButtonLongPressThreshold) {
                mergedOptions.onScanButtonLongPressed(stringWriting.value, scanButtonCounter.value);
            } else if (mergedOptions.onComplete) {
                mergedOptions.onComplete(stringWriting.value, scanButtonCounter.value);
            }
            initScannerDetection();
            return true;
        } else {
            if (mergedOptions.onError) {
                mergedOptions.onError(stringWriting.value);
            }
            initScannerDetection();
            return false;
        }
    };

    const handleKeyDown = (e) => {
        if (mergedOptions.scanButtonKeyCode !== false && e.which === mergedOptions.scanButtonKeyCode) {
            scanButtonCounter.value++;
            e.preventDefault();
            e.stopImmediatePropagation();
        } else if ((firstCharTime.value && mergedOptions.endChar.includes(e.which)) || (!firstCharTime.value && mergedOptions.startChar.includes(e.which))) {
            const e2 = new KeyboardEvent('keypress', e);
            document.dispatchEvent(e2);
            e.preventDefault();
            e.stopImmediatePropagation();
        }
        if (mergedOptions.onKeyDetect) {
            mergedOptions.onKeyDetect(e);
        }
    };

    const handleKeyPress = (e) => {
        if (isFocusOnIgnoredElement()) return;
        if (mergedOptions.stopPropagation) e.stopImmediatePropagation();
        if (mergedOptions.preventDefault) e.preventDefault();
        if (firstCharTime.value && mergedOptions.endChar.includes(e.which)) {
            e.preventDefault();
            e.stopImmediatePropagation();
            callIsScanner.value = true;
        } else if (!firstCharTime.value && mergedOptions.startChar.includes(e.which)) {
            e.preventDefault();
            e.stopImmediatePropagation();
            callIsScanner.value = false;
        } else {
            if (typeof e.which !== 'undefined') {
                stringWriting.value += String.fromCharCode(e.which);
            }
            callIsScanner.value = false;
        }
        if (!firstCharTime.value) {
            firstCharTime.value = Date.now();
        }
        lastCharTime.value = Date.now();
        if (testTimer.value) clearTimeout(testTimer.value);
        if (callIsScanner.value) {
            scannerDetectionTest();
            testTimer.value = null;
        } else {
            testTimer.value = setTimeout(scannerDetectionTest, mergedOptions.timeBeforeScanTest);
        }
        if (mergedOptions.onReceive) {
            mergedOptions.onReceive(e);
        }
    };

    onMounted(() => {
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keypress', handleKeyPress);
    });

    onUnmounted(() => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('keypress', handleKeyPress);
    });

    return {
        initScannerDetection,
        scannerDetectionTest,
    };
}
